import { useState, useCallback } from "react";
import { Button, Input, Space, Badge, Empty, Spin, Alert, Row, Col, Tag, Tooltip, Switch } from "antd";
import { ReloadOutlined, ClearOutlined, ClockCircleOutlined, CheckCircleOutlined, ExclamationCircleOutlined, InfoCircleOutlined, DownOutlined, RightOutlined, ApartmentOutlined } from "@ant-design/icons";
import { AirplaneIcon } from "../../components/icons/SvgIcons";
import useFlightHistory from "../../hooks/useFlightHistory";
import dayjs from "dayjs";
import FlightEventItem from "./FlightEventItem";

const { Search } = Input;

// 业务导向的分类定义 - 按照新的分类要求重新整理
const BUSINESS_CATEGORIES = {
    plan_changes: {
        label: "计划变更",
        description: "航班时刻调整、状态变更、航线变化等计划相关变更",
        color: "#1890ff",
        priority: 1,
        events: ["STATUS_CHANGED", "TIME_UPDATED", "PLAN_REPLACEMENT", "PLAN_INITIATED"],
    },
    flight_info: {
        label: "航班信息",
        description: "MVT航班信息变更、航班属性更新等",
        color: "#52c41a",
        priority: 2,
        events: ["FLIGHT_ATTRIBUTE_UPDATED", "OPERATION_FLIGHT_UPDATED_MVT"],
    },
    permits: {
        label: "飞行许可",
        description: "UAS、民航局、香港民航处等飞行许可申请",
        color: "#fa8c16",
        priority: 3,
        events: ["PERMIT_REQUEST_CREATED", "PERMIT_REQUEST_UPDATED"],
    },
    fuel: {
        label: "燃油保障",
        description: "AEG、Chevron、AFS、EJET等燃油供应商服务",
        color: "#eb2f96",
        priority: 4,
        events: ["FUEL_REQUEST_CREATED", "FUEL_REQUEST_UPDATED"],
    },
    catering: {
        label: "航食配餐",
        description: "各机场航食供应商配餐服务",
        color: "#722ed1",
        priority: 5,
        events: ["CATERING_ORDER_CREATED", "CATERING_ORDER_UPDATED"],
    },
    crew_services: {
        label: "机组服务",
        description: "地面交通、酒店住宿等机组服务",
        color: "#13c2c2",
        priority: 6,
        events: ["CREW_TRANSPORTATION_CREATED", "CREW_TRANSPORTATION_UPDATED", "HOTAC_CREATED", "HOTAC_UPDATED"],
    },
    flight_brief: {
        label: "飞行简报",
        description: "飞行简报生成和发送",
        color: "#f759ab",
        priority: 7,
        events: ["FLIGHT_BRIEF_CREATED", "FLIGHT_BRIEF_UPDATED"],
    },
    other: {
        label: "其他",
        description: "未分类的其他事务类型和通知沟通",
        color: "#8c8c8c",
        priority: 99,
        events: ["EMAIL_SENT", "EMAIL_RECEIVED", "MVT_RECEIVED", "SERVICE_ORDER_CREATED", "SERVICE_ORDER_UPDATED"],
    },
};

// 获取事件的业务分类
const getBusinessCategory = (eventType) => {
    for (const [key, category] of Object.entries(BUSINESS_CATEGORIES)) {
        if (category.events.includes(eventType)) {
            return key;
        }
    }
    return "other"; // 默认分类
};

// 简化的状态图标
const getStatusIcon = (eventType, newValue) => {
    if (eventType.includes("_CREATED")) {
        return <InfoCircleOutlined style={{ color: "#1890ff" }} />;
    }
    if (eventType.includes("_UPDATED")) {
        if (newValue === "CONFIRMED" || newValue === "COMPLETED") {
            return <CheckCircleOutlined style={{ color: "#52c41a" }} />;
        }
        if (newValue === "PENDING" || newValue === "PROCESSING") {
            return <ClockCircleOutlined style={{ color: "#fa8c16" }} />;
        }
        if (newValue === "CANCELLED" || newValue === "FAILED") {
            return <ExclamationCircleOutlined style={{ color: "#ff4d4f" }} />;
        }
    }
    return <InfoCircleOutlined style={{ color: "#8c8c8c" }} />;
};

// 从后端event_description中提取关键信息
const extractKeyInfo = (event) => {
    const { event_type, event_description, additional_info, field_name, previous_value, new_value } = event;

    // 如果有结构化的附加信息，优先使用
    if (additional_info) {
        // 尝试从additional_info中提取关键信息
        if (additional_info.user_friendly_description) {
            return extractFromUserFriendlyDescription(additional_info.user_friendly_description);
        }

        // 从previous_data和new_data中提取变更信息
        if (additional_info.previous_data && additional_info.new_data) {
            return extractFromDataChanges(additional_info.previous_data, additional_info.new_data);
        }
    }

    // 从event_description中提取关键信息
    if (event_description) {
        return extractFromEventDescription(event_description);
    }

    // 从基础字段中提取信息
    if (field_name && previous_value && new_value) {
        return {
            summary: getFieldChangeDisplay(field_name, previous_value, new_value),
            details: null,
        };
    }

    // 默认返回
    return {
        summary: getDefaultEventSummary(event_type),
        details: null,
    };
};

// 从用户友好描述中提取关键信息
const extractFromUserFriendlyDescription = (description) => {
    if (!description) return { summary: null, details: null };

    // 移除Markdown格式，提取核心信息
    const cleanDesc = description
        .replace(/#{1,3}\s*/g, "")
        .replace(/\*\*/g, "")
        .replace(/`([^`]+)`/g, "$1");

    // 提取状态变更信息
    const statusMatch = cleanDesc.match(/状态[：:]\s*(.+?)\s*→\s*(.+?)(?:\n|$)/);
    if (statusMatch) {
        return {
            summary: `状态: ${statusMatch[1]} → ${statusMatch[2]}`,
            details: cleanDesc,
        };
    }

    // 提取时间变更信息
    const timeMatch = cleanDesc.match(/(计划|预计|实际).+?时间[：:]\s*(.+?)\s*→\s*(.+?)(?:\n|$)/);
    if (timeMatch) {
        return {
            summary: `${timeMatch[1]}时间: ${timeMatch[2]} → ${timeMatch[3]}`,
            details: cleanDesc,
        };
    }

    // 提取创建信息
    if (cleanDesc.includes("创建")) {
        const lines = cleanDesc.split("\n");
        const firstLine = lines[0] || "";
        const statusLine = lines.find((line) => line.includes("状态:"));
        const summary = statusLine ? `${firstLine} (${statusLine.trim()})` : firstLine;

        return {
            summary: summary.length > 50 ? summary.substring(0, 50) + "..." : summary,
            details: cleanDesc,
        };
    }

    // 默认提取前两行作为摘要
    const lines = cleanDesc.split("\n").filter((line) => line.trim());
    const summary = lines.slice(0, 2).join(" ").trim();

    return {
        summary: summary.length > 80 ? summary.substring(0, 80) + "..." : summary,
        details: cleanDesc,
    };
};

// 从数据变更中提取关键信息
const extractFromDataChanges = (previousData, newData) => {
    const changes = [];

    // 检查状态变更
    if (previousData.status !== newData.status) {
        changes.push(`状态: ${previousData.status || "无"} → ${newData.status || "无"}`);
    }

    // 检查备注变更
    if (previousData.remarks !== newData.remarks) {
        const oldRemarks = previousData.remarks || "无";
        const newRemarks = newData.remarks || "无";
        if (oldRemarks.length > 20) oldRemarks = oldRemarks.substring(0, 20) + "...";
        if (newRemarks.length > 20) newRemarks = newRemarks.substring(0, 20) + "...";
        changes.push(`备注: ${oldRemarks} → ${newRemarks}`);
    }

    // 检查详情变更
    if (previousData.details && newData.details) {
        const oldDetails = previousData.details;
        const newDetails = newData.details;

        // 检查订单号变更
        if (oldDetails.orderNumber !== newDetails.orderNumber) {
            changes.push(`订单号: ${oldDetails.orderNumber || "无"} → ${newDetails.orderNumber || "无"}`);
        }

        // 检查过期日期变更
        if (oldDetails.expiryDate !== newDetails.expiryDate) {
            changes.push(`过期日期: ${oldDetails.expiryDate || "无"} → ${newDetails.expiryDate || "无"}`);
        }
    }

    return {
        summary: changes.length > 0 ? changes[0] : "记录已更新",
        details: changes.length > 1 ? changes.join("\n") : null,
    };
};

// 从事件描述中提取关键信息
const extractFromEventDescription = (description) => {
    if (!description) return { summary: null, details: null };

    // 移除Markdown格式
    const cleanDesc = description
        .replace(/#{1,3}\s*/g, "")
        .replace(/\*\*/g, "")
        .replace(/`([^`]+)`/g, "$1");

    // 提取第一行作为摘要
    const lines = cleanDesc.split("\n").filter((line) => line.trim());
    const summary = lines[0] || "";

    return {
        summary: summary.length > 60 ? summary.substring(0, 60) + "..." : summary,
        details: cleanDesc,
    };
};

// 字段变更显示
const getFieldChangeDisplay = (fieldName, previousValue, newValue) => {
    const fieldDisplayNames = {
        status: "状态",
        std: "计划起飞时间",
        sta: "计划到达时间",
        etd: "预计起飞时间",
        eta: "预计到达时间",
        atd: "实际起飞时间",
        ata: "实际到达时间",
        delay_code: "延误代码",
        delay_minutes: "延误时长",
        remarks: "备注",
    };

    const displayName = fieldDisplayNames[fieldName] || fieldName;
    const prev = previousValue || "无";
    const curr = newValue || "无";

    return `${displayName}: ${prev} → ${curr}`;
};

// 默认事件摘要
const getDefaultEventSummary = (eventType) => {
    const eventDisplayNames = {
        FUEL_REQUEST_CREATED: "创建燃油申请",
        FUEL_REQUEST_UPDATED: "更新燃油申请",
        PERMIT_REQUEST_CREATED: "创建飞行许可申请",
        PERMIT_REQUEST_UPDATED: "更新飞行许可申请",
        CATERING_ORDER_CREATED: "创建配餐订单",
        CATERING_ORDER_UPDATED: "更新配餐订单",
        CREW_TRANSPORTATION_CREATED: "创建机组接送",
        CREW_TRANSPORTATION_UPDATED: "更新机组接送",
        HOTAC_CREATED: "创建酒店住宿",
        HOTAC_UPDATED: "更新酒店住宿",
        FLIGHT_BRIEF_CREATED: "创建飞行简报",
        FLIGHT_BRIEF_UPDATED: "更新飞行简报",
        FLIGHT_ATTRIBUTE_UPDATED: "更新航班属性",
        STATUS_CHANGED: "航班状态变更",
        TIME_UPDATED: "航班时间更新",
        OPERATION_FLIGHT_UPDATED_MVT: "MVT报文更新",
        EMAIL_SENT: "发送邮件",
        SERVICE_ORDER_CREATED: "创建服务订单",
        SERVICE_ORDER_UPDATED: "更新服务订单",
    };

    return eventDisplayNames[eventType] || eventType;
};

// 获取供应商或机场信息
const getBusinessContext = (event) => {
    const { additional_info, related_business_type } = event;

    if (additional_info) {
        // 从燃油申请中提取供应商信息
        if (related_business_type === "FuelRequest" && additional_info.fuel_request_data) {
            const details = additional_info.fuel_request_data.details;
            if (details && details.supplier) {
                return details.supplier;
            }
        }

        // 从配餐订单中提取供应商信息
        if (related_business_type === "CateringOrder" && additional_info.catering_order_data) {
            const details = additional_info.catering_order_data.details;
            if (details && details.supplier) {
                return details.supplier;
            }
        }

        // 从服务订单中提取供应商信息
        if (additional_info.partner_name) {
            return additional_info.partner_name;
        }
    }

    return null;
};

/**
 * 业务导向的航班历史时间线组件
 * @param {number} flightId - 航班ID
 * @param {function} onRelatedObjectClick - 相关对象点击回调
 * @param {object} style - 样式
 * @param {string|string[]} allowedCategories - 允许显示的分类，可以是单个分类或分类数组，不传则显示所有分类
 */
const FlightHistoryTimeline = ({ flightId, onRelatedObjectClick, style = {}, allowedCategories = null }) => {
    // 处理允许的分类参数
    const normalizedAllowedCategories = allowedCategories ? (Array.isArray(allowedCategories) ? allowedCategories : [allowedCategories]) : null;

    // 总是默认激活"全部"分类，即使指定了允许的分类
    const defaultActiveCategory = "all";

    const [activeCategory, setActiveCategory] = useState(defaultActiveCategory);

    const { events, total, loading, loadingMore, error, businessEntityEnabled, businessEntityId, flightChainInfo, updateFilters, resetFilters, loadMore, refresh, setSearchQuery, toggleBusinessEntity } = useFlightHistory(flightId, { include_business_entity: true });

    // 按业务分类组织事件（只统计允许的分类）
    const eventsByCategory = events.reduce((acc, event) => {
        const category = getBusinessCategory(event.event_type);

        // 如果指定了允许的分类，只统计这些分类的事件
        if (normalizedAllowedCategories && !normalizedAllowedCategories.includes(category)) {
            return acc;
        }

        if (!acc[category]) {
            acc[category] = [];
        }
        acc[category].push(event);
        return acc;
    }, {});

    // 处理分类切换
    const handleCategoryChange = useCallback(
        (category) => {
            // 如果指定了允许的分类，检查是否在允许范围内
            if (normalizedAllowedCategories && !normalizedAllowedCategories.includes(category) && category !== "all") {
                return; // 不允许切换到未授权的分类
            }

            setActiveCategory(category);
            if (category === "all") {
                updateFilters({ category: "all" });
            } else {
                // 将业务分类映射到后端的事件分类
                const businessCategory = BUSINESS_CATEGORIES[category];
                if (businessCategory) {
                    // 根据前端业务分类映射到后端分类
                    let backendCategory = "all";

                    switch (category) {
                        case "plan_changes":
                        case "flight_info":
                            backendCategory = "plan";
                            break;
                        case "permits":
                        case "fuel":
                        case "catering":
                        case "crew_services":
                        case "flight_brief":
                            backendCategory = "transaction";
                            break;
                        case "other":
                            backendCategory = "communication";
                            break;
                        default:
                            backendCategory = "all";
                    }

                    updateFilters({ category: backendCategory });
                } else {
                    updateFilters({ category: "all" });
                }
            }
        },
        [updateFilters, normalizedAllowedCategories],
    );

    // 处理搜索
    const handleSearch = useCallback(
        (value) => {
            setSearchQuery(value.trim());
        },
        [setSearchQuery],
    );

    // 处理业务主体开关
    const handleBusinessEntityToggle = useCallback(
        (enabled) => {
            toggleBusinessEntity(enabled);
        },
        [toggleBusinessEntity],
    );

    // 过滤显示的事件 - 现在主要依赖后端筛选，前端只做补充过滤
    const filteredEvents = (() => {
        let filtered = events;

        // 首先根据允许的分类进行过滤
        if (normalizedAllowedCategories) {
            filtered = filtered.filter((event) => {
                const eventCategory = getBusinessCategory(event.event_type);
                return normalizedAllowedCategories.includes(eventCategory);
            });
        }

        // 然后根据当前激活的分类进行过滤
        if (activeCategory !== "all") {
            filtered = filtered.filter((event) => getBusinessCategory(event.event_type) === activeCategory);
        }

        return filtered;
    })();

    // 获取航班信息显示
    const getFlightDisplayInfo = (event) => {
        if (!businessEntityEnabled || !flightChainInfo) {
            return null;
        }

        const flightInfo = flightChainInfo[event.flight_id];
        if (!flightInfo) {
            return null;
        }

        const isCurrentFlight = event.flight_id === flightId;
        const displayText = `${flightInfo.flight_number} (${flightInfo.departure_airport}-${flightInfo.arrival_airport})`;

        return {
            ...flightInfo,
            displayText,
            isCurrentFlight,
            tagColor: isCurrentFlight ? "#1890ff" : "#87d068",
        };
    };

    // 渲染分类过滤器
    const renderCategoryFilters = () => {
        const allCount = events.length;

        // 如果指定了允许的分类，只显示这些分类
        const categoriesToShow = normalizedAllowedCategories ? Object.entries(BUSINESS_CATEGORIES).filter(([key]) => normalizedAllowedCategories.includes(key)) : Object.entries(BUSINESS_CATEGORIES);

        // 如果只有一个分类且不是"all"，则不显示过滤器
        if (normalizedAllowedCategories && normalizedAllowedCategories.length === 1 && !normalizedAllowedCategories.includes("all")) {
            return null;
        }

        return (
            <Space wrap style={{ marginBottom: 16 }}>
                {/* 总是显示全部按钮，即使指定了允许的分类 */}
                <Button type={activeCategory === "all" ? "primary" : "default"} size="small" onClick={() => handleCategoryChange("all")}>
                    全部 ({allCount})
                </Button>
                {categoriesToShow.map(([key, category]) => {
                    const count = eventsByCategory[key]?.length || 0;
                    return (
                        <Tooltip key={key} title={category.description}>
                            <Button
                                type={activeCategory === key ? "primary" : "default"}
                                size="small"
                                onClick={() => handleCategoryChange(key)}
                                style={{
                                    borderColor: activeCategory === key ? undefined : category.color,
                                    color: activeCategory === key ? undefined : category.color,
                                }}
                            >
                                {category.label} ({count})
                            </Button>
                        </Tooltip>
                    );
                })}
            </Space>
        );
    };

    // 渲染优化的事件条目
    const renderOptimizedEvent = (event) => {
        // 定义需要使用FlightEventItem组件的事件类型
        const useFlightEventItemTypes = [
            "EMAIL_SENT", // 邮件事件
            "FLIGHT_ATTRIBUTE_UPDATED", // 航班变更事件
            "PLAN_INITIATED",
            "STATUS_CHANGED",
            "TIME_UPDATED",
            "PLAN_REPLACEMENT",
            "OPERATION_FLIGHT_UPDATED_MVT",
        ];

        // 对于特殊事件，使用FlightEventItem组件以支持特殊功能（邮件查看、变更对比等）
        if (useFlightEventItemTypes.includes(event.event_type)) {
            return <FlightEventItem key={event.id} event={event} onRelatedObjectClick={onRelatedObjectClick} />;
        }

        // 其他事件使用原有的渲染逻辑
        const keyInfo = extractKeyInfo(event);
        const icon = getStatusIcon(event.event_type, event.new_value);
        const category = BUSINESS_CATEGORIES[getBusinessCategory(event.event_type)];
        const time = dayjs(event.created_at).format("MM-DD HH:mm");
        const businessContext = getBusinessContext(event);
        const flightDisplayInfo = getFlightDisplayInfo(event);

        return (
            <div
                key={event.id}
                style={{
                    padding: "12px 16px",
                    border: "1px solid #f0f0f0",
                    borderRadius: "6px",
                    marginBottom: "8px",
                    backgroundColor: "#fafafa",
                    borderLeft: `4px solid ${category.color}`,
                }}
            >
                {/* 第一行：图标 + 主要信息 + 时间 */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", flex: 1 }}>
                        {icon}
                        <span style={{ fontWeight: 500, fontSize: "14px" }}>{keyInfo.summary || getDefaultEventSummary(event.event_type)}</span>
                        <Tag color={category.color} size="small" style={{ margin: 0 }}>
                            {category.label}
                        </Tag>
                        {/* 航班来源标识 */}
                        {flightDisplayInfo && (
                            <Tag color={flightDisplayInfo.tagColor} size="small" icon={<AirplaneIcon />} style={{ margin: 0 }}>
                                {flightDisplayInfo.displayText}
                            </Tag>
                        )}
                    </div>
                    <span style={{ color: "#8c8c8c", fontSize: "12px", marginLeft: "8px" }}>{time}</span>
                </div>

                {/* 第二行：业务上下文信息（如果有） */}
                {(businessContext || event.operator || (businessEntityEnabled && businessEntityId)) && (
                    <div
                        style={{
                            marginTop: "4px",
                            marginLeft: "24px",
                            fontSize: "12px",
                            color: "#666",
                        }}
                    >
                        <Space size="small">
                            {businessContext && <span>供应商: {businessContext}</span>}
                            {event.operator && <span>操作者: {event.operator}</span>}
                            {event.related_business_id && <span>#{event.related_business_id}</span>}
                            {businessEntityEnabled && businessEntityId && event.flight_id !== flightId && (
                                <span style={{ color: "#1890ff" }}>
                                    <ApartmentOutlined style={{ marginRight: 4 }} />
                                    业务主体 #{businessEntityId}
                                </span>
                            )}
                        </Space>
                    </div>
                )}

                {/* 详细信息展开 */}
                {keyInfo.details && (
                    <div style={{ marginTop: "8px", marginLeft: "24px" }}>
                        <EventDetailsCollapse details={keyInfo.details} eventDescription={event.event_description} />
                    </div>
                )}
            </div>
        );
    };

    // 渲染按时间分组的事件
    const renderGroupedEvents = () => {
        if (loading) {
            return <Spin spinning={true} tip="加载中..." style={{ display: "block", textAlign: "center", padding: "20px" }} />;
        }

        if (filteredEvents.length === 0) {
            return <Empty description="暂无相关事件" />;
        }

        // 按日期分组
        const eventsByDate = filteredEvents.reduce((acc, event) => {
            const date = dayjs(event.created_at).format("YYYY-MM-DD");
            if (!acc[date]) {
                acc[date] = [];
            }
            acc[date].push(event);
            return acc;
        }, {});

        return (
            <div>
                {Object.entries(eventsByDate)
                    .sort(([a], [b]) => b.localeCompare(a)) // 最新日期在前
                    .map(([date, events]) => (
                        <div key={date} style={{ marginBottom: "20px" }}>
                            <div
                                style={{
                                    padding: "8px 0",
                                    borderBottom: "1px solid #e8e8e8",
                                    marginBottom: "12px",
                                    color: "#262626",
                                    fontWeight: 500,
                                }}
                            >
                                {dayjs(date).format("YYYY年MM月DD日")}
                            </div>
                            {events.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).map(renderOptimizedEvent)}
                        </div>
                    ))}

                {loadingMore && (
                    <div style={{ textAlign: "center", padding: "20px" }}>
                        <Spin spinning={true} tip="加载更多..." />
                    </div>
                )}

                {!loadingMore && !normalizedAllowedCategories && filteredEvents.length < total && (
                    <div style={{ textAlign: "center", padding: "20px" }}>
                        <Button onClick={loadMore} type="dashed">
                            加载更多 ({filteredEvents.length}/{total})
                        </Button>
                    </div>
                )}
            </div>
        );
    };

    if (error) {
        return (
            <Alert
                message="加载失败"
                description={error}
                type="error"
                showIcon
                action={
                    <Button size="small" onClick={refresh}>
                        重试
                    </Button>
                }
            />
        );
    }

    return (
        <div style={style}>
            {/* 工具栏 */}
            <div style={{ marginBottom: 16 }}>
                <Row gutter={[16, 8]} align="middle">
                    <Col flex="auto">
                        <Search placeholder="搜索事件内容..." allowClear onSearch={handleSearch} style={{ maxWidth: 300 }} />
                    </Col>
                    <Col>
                        <Space>
                            <Badge count={total} showZero color="#1890ff">
                                <span style={{ fontSize: "12px", color: "#666" }}>总事件</span>
                            </Badge>
                            <Button icon={<ReloadOutlined />} onClick={refresh} size="small" loading={loading}>
                                刷新
                            </Button>
                            <Button icon={<ClearOutlined />} onClick={resetFilters} size="small">
                                重置
                            </Button>
                        </Space>
                    </Col>
                </Row>
            </div>

            {/* 分类过滤器 */}
            {renderCategoryFilters()}

            {/* 业务主体开关 */}
            <div style={{ marginBottom: 16, marginTop: 16 }}>
                <Row align="middle" gutter={16}>
                    <Col>
                        <Tooltip title={businessEntityEnabled ? "显示该航班及其替换链中所有航班的事件" : "只显示当前航班的事件"}>
                            <Switch checkedChildren={<ApartmentOutlined />} unCheckedChildren={<AirplaneIcon />} checked={businessEntityEnabled} onChange={handleBusinessEntityToggle} />
                        </Tooltip>
                    </Col>
                    <Col>
                        <span style={{ fontSize: "14px", fontWeight: 500 }}>{businessEntityEnabled ? "业务主体视图" : "单一航班视图"}</span>
                    </Col>
                    {businessEntityEnabled && businessEntityId && (
                        <Col>
                            <Tag color="#1890ff" style={{ margin: 0 }}>
                                业务主体 #{businessEntityId}
                            </Tag>
                        </Col>
                    )}
                    {businessEntityEnabled && flightChainInfo && Object.keys(flightChainInfo).length > 1 && (
                        <Col>
                            <Tag color="#87d068" style={{ margin: 0 }}>
                                包含 {Object.keys(flightChainInfo).length} 个航班
                            </Tag>
                        </Col>
                    )}
                </Row>
                {businessEntityEnabled && flightChainInfo && Object.keys(flightChainInfo).length > 1 && (
                    <div style={{ marginTop: 8, fontSize: "12px", color: "#666" }}>
                        <Space wrap>
                            {Object.entries(flightChainInfo).map(([chainFlightId, flightInfo]) => (
                                <span key={chainFlightId}>
                                    {flightInfo.flight_number} ({flightInfo.departure_airport}-{flightInfo.arrival_airport}){parseInt(chainFlightId) === parseInt(flightId) && <span style={{ color: "#1890ff" }}> (当前)</span>}
                                </span>
                            ))}
                        </Space>
                    </div>
                )}
            </div>

            {/* 事件列表 */}
            {renderGroupedEvents()}
        </div>
    );
};

// 详细信息折叠组件
const EventDetailsCollapse = ({ details, eventDescription }) => {
    const [showDetails, setShowDetails] = useState(false);

    return (
        <div>
            <Button type="link" size="small" icon={showDetails ? <DownOutlined /> : <RightOutlined />} onClick={() => setShowDetails(!showDetails)} style={{ padding: 0, height: "auto", fontSize: "12px" }}>
                {showDetails ? "收起详情" : "查看详情"}
            </Button>

            {showDetails && (
                <div
                    style={{
                        marginTop: "8px",
                        padding: "8px 12px",
                        backgroundColor: "#fff",
                        borderRadius: 4,
                        border: "1px solid #f0f0f0",
                        fontSize: "12px",
                        whiteSpace: "pre-wrap",
                    }}
                >
                    {details || eventDescription}
                </div>
            )}
        </div>
    );
};

export default FlightHistoryTimeline;
